import type { Filter, ResolvedMonospacePrimitiveField } from '#layers/data-model/public';
import { Temporal } from 'temporal-polyfill';
import { createFieldFilter, FilterOperator, getTypename, isPrimitiveTypeDateTime, mergeAnd, PRIMITIVE_TYPE_DATE, PRIMITIVE_TYPE_DATE_TIME_WITH_TIMEZONE } from '#layers/data-model/public';

type RangeEdge = 'start' | 'end';
type CanonicalDateOperand = Temporal.PlainDate | Temporal.PlainDateTime | Temporal.ZonedDateTime;

const START_OF_DAY = Temporal.PlainTime.from('00:00:00');
const END_OF_DAY = Temporal.PlainTime.from('23:59:59.999999999');

export interface CalendarRange {
	start: Temporal.PlainDate; // inclusive first visible day
	end: Temporal.PlainDate; // inclusive last visible day
}

/**
 * Canonical Temporal operand for one edge of the visible slice, in the field's own primitive type —
 * the type the rstore fetch boundary asserts before serializing to wire values. Datetime fields take
 * start-/end-of-day from the edge to keep the slice inclusive; zoned fields resolve in UTC.
 * Returns null when the field is not a date/datetime type.
 */
export function rangeBoundaryOperand(day: Temporal.PlainDate, field: ResolvedMonospacePrimitiveField, edge: RangeEdge): CanonicalDateOperand | null {
	const typeName = getTypename(field.type);

	if (typeName === PRIMITIVE_TYPE_DATE) return day;
	if (!isPrimitiveTypeDateTime(typeName)) return null;

	const time = edge === 'start' ? START_OF_DAY : END_OF_DAY;
	if (typeName === PRIMITIVE_TYPE_DATE_TIME_WITH_TIMEZONE) return day.toZonedDateTime({ timeZone: 'UTC', plainTime: time });
	return day.toPlainDateTime(time);
}

export interface BuildCalendarRangeFilterOptions {
	startField: ResolvedMonospacePrimitiveField;
	range: CalendarRange;
	endField?: ResolvedMonospacePrimitiveField;
}

/**
 * Builds the calendar's visible-range filter as an interval-overlap predicate:
 * event.start <= rangeEnd AND event.end >= rangeStart. Falls back to the start field when no end
 * field is configured (single-day events), collapsing to a single-field window in that case.
 * Returns null when a boundary operand cannot be built (non-date field type).
 */
export function buildCalendarRangeFilter({ startField, range, endField }: BuildCalendarRangeFilterOptions): Filter | null {
	const upper = rangeBoundaryOperand(range.end, startField, 'end'); // event.start <= rangeEnd
	const lowerField = endField ?? startField;
	const lower = rangeBoundaryOperand(range.start, lowerField, 'start'); // event.end >= rangeStart
	if (upper == null || lower == null) return null;

	if (lowerField.apiName === startField.apiName) {
		// `as Filter`: FieldFilterOperator types operands as string|number (has a `// TODO refine`);
		// widening that shared type is out of scope, so the object literal keeps the cast.
		return { [startField.apiName]: { [FilterOperator.GTE]: lower, [FilterOperator.LTE]: upper } } as Filter;
	}

	return mergeAnd(
		createFieldFilter([startField.apiName], FilterOperator.LTE, upper),
		createFieldFilter([lowerField.apiName], FilterOperator.GTE, lower),
	);
}
